import authService from '../services/api/auth/authService';
import websocketService from '../services/websocket';
import { clearTokens } from './tokenStorage';
import { clearEndpoints } from './endpointStorage';

// Create a flag to track if logout is in progress to prevent duplicate calls
let logoutInProgress = false;

// Re-export token functions for backward compatibility
export { 
  getAccessToken, 
  getRefreshToken, 
  setTokens, 
  setUserData, 
  getUserData, 
  isAuthenticated 
} from './tokenStorage';

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password strength (min 8 chars, at least 1 letter and 1 number)
export const isStrongPassword = (password) => {
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
  return passwordRegex.test(password);
};

/**
 * Clear all authentication data
 * Use this for logout or when auth is invalid
 */
export const clearAuth = async () => {
  // If already logging out, return the same promise to prevent duplicate calls
  if (logoutInProgress) {
    console.log('Logout already in progress, preventing duplicate calls');
    return Promise.resolve({ 
      success: true, 
      message: 'Logout already in progress',
      alreadyInProgress: true 
    });
  }
  
  // Set flag to prevent duplicate calls
  logoutInProgress = true;
  
  try {
    console.log('Clearing authentication data');
    
    // First try to call the logout endpoint while we still have valid tokens
    try {
      const logoutResult = await authService.logout();
      if (logoutResult.status === 'already-logged-out') {
        console.log('Token was already expired, local logout only');
      } else {
        console.log('Successfully logged out on server');
      }
    } catch (error) {
      // Just log this, don't alert the user - we'll clean up locally regardless
      console.error('Error during server logout:', error.message || 'Unknown error');
      // Continue with local logout even if server logout fails
    }
    
    // Then disconnect WebSocket with a clearer message
    console.log('Forcing WebSocket disconnection');
    websocketService.forceDisconnect();
    
    // Clear endpoints from storage with clearer logging
    console.log('Clearing stored endpoints and timestamp');
    clearEndpoints();
    
    // Finally clear tokens from storage
    console.log('Removing authentication tokens from storage');
    clearTokens();
    
    // Return a resolved promise to indicate cleanup is complete
    return Promise.resolve({ success: true, message: 'Logout complete' });
  } finally {
    // Reset the flag regardless of success or failure
    setTimeout(() => {
      logoutInProgress = false;
    }, 500); // Small delay to prevent rapid consecutive calls
  }
}; 